/**
 * On-device storage. IndexedDB, never uploaded.
 *
 * This holds the two things that must not leave the device: the speaker template
 * and the signing keypair. [M] gives the reason the template stays put, and it is
 * not primarily a privacy one -- a template is only reliable against audio from
 * the device that enrolled it. The same speaker in one conversation had 313 s of
 * speech attributed from one phone and 101 s from the other. Moved elsewhere, a
 * template is not just a liability, it is close to useless.
 *
 * A consequence worth surfacing in the UI: a new phone is a new device and needs
 * its own enrollment. There is no migration path and there should not be one.
 */

import { SAMPLE_RATE } from "./const.js";

const DB_NAME = "meetfolks";
const DB_VERSION = 1;
const STORE = "device";

function open() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function put(key, value) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function get(key) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function del(key) {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// -- keypair -----------------------------------------------------------
// CryptoKey objects are structured-cloneable, so a non-extractable private key
// can be persisted as a live handle: usable for signing, never readable as bytes.

export const saveKeypair = (kp) => put("keypair", kp);
export const loadKeypair = () => get("keypair");

// -- template ----------------------------------------------------------
// Stored as the individual takes plus the centroid. Keeping the takes is what
// makes enrollment incremental: a correction (§12.6) becomes an extra take and a
// local recomputation, because the audio never left the device.

export async function saveTemplate(template) {
  await put("template", template);
}

export const loadTemplate = () => get("template");
export const clearTemplate = () => del("template");

/** Freshly enrolled profile: takes are `{vector, seconds, capturedAt}`. */
export function makeTemplate(takes, centroid, channel) {
  return {
    takes,
    centroid,
    channel,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    model: "speechbrain/spkrec-ecapa-voxceleb",
    sampleRate: SAMPLE_RATE,
  };
}

// -- server credentials ------------------------------------------------

export const saveCredentials = (creds) => put("credentials", creds);
export const loadCredentials = () => get("credentials");
export const clearCredentials = () => del("credentials");

/** Everything, gone. Offered in the UI because biometric data needs an exit. */
export async function wipe() {
  for (const key of ["keypair", "template", "credentials", "lastSession", "pushSync"])
    await del(key);
}

/** The whole device store, gone. The nuclear option for a full sign-out. */
export async function wipeAll() {
  const db = await open();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export const saveLastSession = (s) => put("lastSession", s);
export const loadLastSession = () => get("lastSession");

/**
 * Notifications the service worker handled while no app was running.
 *
 * The only way the "was it actually delivered?" half of push telemetry can be
 * observed at all. A push arrives in the service worker, which has no device
 * token -- that lives behind the app -- so it cannot report anything itself. It
 * writes here instead, and `engine/push.ts` drains this on the next boot.
 *
 * Written by `sw.js` directly rather than through this module, because a service
 * worker cannot import it. The key and the shape are the contract between the
 * two; see `recordPushEvent` there, which mirrors this deliberately.
 */
export const loadPushLog = () => get("pushLog");
export const clearPushLog = () => del("pushLog");

/**
 * The last push endpoint this device successfully registered with the server.
 *
 * `syncSubscription` skips its POST when the browser's endpoint still matches
 * this record, so a boot does not rewrite a byte-identical server row (#434).
 * The record is written only after the server's answer, so a failed sync is
 * retried on the next boot; it is cleared by `wipe`, because a re-registered
 * device is a new identity the server must be told about even at an unchanged
 * endpoint.
 */
export const savePushSyncRecord = (record) => put("pushSync", record);
export const loadPushSyncRecord = () => get("pushSync");

/**
 * Lowest pairwise cosine between takes, or null with fewer than two.
 *
 * [D] Takes of one person should agree. The measured whole-clip same-speaker floor
 * was 0.603 and the best different-speaker pair 0.414, so a pair below ~0.45 is
 * more likely two different people, a mislabelled take, or one take that is mostly
 * someone else talking.
 */
export function takeAgreement(takes, cosine) {
  if (!takes || takes.length < 2) return null;
  let worst = 1;
  for (let i = 0; i < takes.length; i++) {
    for (let j = i + 1; j < takes.length; j++) {
      const s = cosine(takes[i].vector, takes[j].vector);
      if (s < worst) worst = s;
    }
  }
  return worst;
}
